import type { MouseEvent } from 'react'
import type { VodWithPrice } from '~/shared/interfaces'
import type { ViewRow } from '../../model/components/index.h'

import { CardAnimations } from "/app/libraries/arcana/src/blocks/Cards/index.ts"
import { TopVodSimpleCard, VodSimpleCard } from "/app/libraries/arcana/src/blocks/Cards/Templates/index.ts"

import { useUnit } from 'effector-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isPaid } from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { addQuery } from '~/shared/routes'
import { resolveNewLabel } from '~/shared/tools'
import {
  VodCardHorizontalCoversMap,
  VodCardVerticalCoversMap,
} from '~/shared/ui/components/cardCovers'
import {
  getCardContentPadding,
  getCategoryId,
  getCollectionId,
  getOnlyFavorites,
} from '../lib/style'

export const VodCard = ({
  row,
  item,
  idx,
}: {
  row: ViewRow
  item: VodWithPrice
  idx: number
}) => {
  const {
    imageUrl,
    imageHorizontalUrl,
    rented,
    locked,
    name,
    id,
    labelPrice,
    ageRatings,
    blockedByAcl,
    labels,
  } = item

  const {
    appearance: {
      style: { posterSize, posterOrientation, topContent },
      resource: { search },
      title,
    },
  } = row

  const { isLocked, intercept } = useUnit({
    isLocked: pinLockFeature.$isLockedFn,
    intercept: pinLockFeature.intercept,
  })

  const { t } = useTranslation()

  // Pin-locked posters already replace their whole content via BaseCard's
  // own `locked` handling, so no extra guard is needed here.
  const newLabel = resolveNewLabel({ labels }, t)

  const categoryId = getCategoryId(search)
  const collectionId = getCollectionId(search)
  const onlyFavorites = getOnlyFavorites(search)

  const handleVodCardClick = useCallback(
    (event: MouseEvent<Element>) => {
      event.preventDefault()
      event.stopPropagation()
      intercept({
        id,
        locked,
        name,
        '<event>': event,
      })
    },
    [intercept, id, locked, name]
  )

  const contentPadding = getCardContentPadding(posterSize)

  const posterUrl =
    posterOrientation === 'HORIZONTAL' ? imageHorizontalUrl : imageUrl
  const covers =
    posterOrientation === 'HORIZONTAL'
      ? VodCardHorizontalCoversMap
      : VodCardVerticalCoversMap

  const Card = topContent ? TopVodSimpleCard : VodSimpleCard
  const topNumber = idx + 1

  const link = useMemo(
    () =>
      addQuery(`/movies/${id}`, {
        categoryId,
        collectionId,
        'only-favorites': onlyFavorites || undefined,
        categoryName: title,
      }),
    [id, categoryId, collectionId, onlyFavorites, title]
  )

  return (
    <a href={link}>
      <Card
        onClick={handleVodCardClick}
        childrenTransition={CardAnimations.FadeIn}
        lazyImage={false}
        type={posterOrientation}
        posterUrl={posterUrl}
        paid={isPaid(item)}
        rented={rented}
        locked={isLocked({ id, locked })}
        aclBlockedBy={Boolean(blockedByAcl)}
        title={name}
        purchaseTitle={labelPrice}
        contentPadding={contentPadding}
        covers={covers}
        ageRatingLabel={ageRatings}
        topNumber={topNumber}
        newLabel={newLabel}
      />
    </a>
  )
}
